import { z } from 'zod';
import { EmailDeliveryStatus, Id, InterventionStatus, queryList, Timestamp } from './common.js';

/**
 * 02/10/2026 : « E-mails envoyés » (Compte → Notifications). Journal de **tous** les e-mails expédiés par
 * l'outil pour le garage actif (`email_deliveries`) : e-mails au client (dossier, rendez-vous), e-mails à
 * l'équipe (question, décision, bilan, accès support) et invitations. Jamais les e-mails de compte
 * personnel (réinitialisation du mot de passe : hors journal du garage, et exclus explicitement).
 *
 * Droit : `team.manage` (propriétaire et associé ; jamais le support). Destinataire en clair pour ce droit,
 * `ERASED_MARK` après effacement du client (`customer_erase`), contenu alors non consultable.
 */

/** Taille de page par défaut de `deliveries.search`. */
export const SENT_EMAILS_PAGE_SIZE = 30;

/** Destinataires : le client (ou futur client), ou l'équipe du garage (y compris une personne invitée). */
export const SentEmailAudience = z.enum(['customer', 'garage']);
export type SentEmailAudience = z.infer<typeof SentEmailAudience>;

/** Modèles adressés à l'équipe du garage ; tout autre modèle est un e-mail au client. */
export const GARAGE_AUDIENCE_TEMPLATES: readonly string[] = [
  'notifications.complement_decided',
  'notifications.question_asked',
  'notifications.support_requested',
  'notifications.support_ended',
  'notifications.weekly_digest',
  'team.invitation',
];

/** Modèles de compte personnel, jamais listés dans le journal d'un garage. */
export const PERSONAL_ACCOUNT_TEMPLATES: readonly string[] = ['auth.password_reset'];

export const sentEmailAudience = (template: string): SentEmailAudience =>
  GARAGE_AUDIENCE_TEMPLATES.includes(template) ? 'garage' : 'customer';

/** Motifs techniques d'annulation (codes) → libellé lisible ; les autres motifs sont déjà en français. */
export const DELIVERY_ERROR_LABELS: Record<string, string> = {
  garage_suspended: 'Annulé : garage suspendu',
  demo_garage: 'Annulé : garage de démonstration (aucun envoi réel)',
  customer_erased: 'Annulé : données du client effacées',
  customer_opted_out: 'Annulé : le client s’est opposé aux e-mails',
};

export const deliveryErrorLabel = (error: string | null): string | null =>
  error === null ? null : (DELIVERY_ERROR_LABELS[error] ?? error);

/**
 * `deliveries.search` : filtres cumulables, côté serveur.
 * - `q` : adresse destinataire, nom du client ou plaque (sans casse ni accents ; plaque comparée sous forme
 *   compacte, `AB123CD` trouve `AB-123-CD`) ;
 * - `recipient`, `customer`, `plate` : même règle, champ par champ ;
 * - `customerId`, `vehicleId`, `interventionId` : un client, un véhicule ou un dossier précis (identifiant d'un
 *   autre garage : liste vide) ;
 * - `template`, `status` : clés répétées ;
 * - `from` / `to` : jours du fuseau du garage, bornes incluses, sur la date d'envoi (sinon d'inscription) ;
 * - `cursor` : curseur opaque (`nextCursor`) ; tri du plus récent au plus ancien.
 */
export const SentEmailsQuery = z.strictObject({
  q: z.string().trim().max(100).optional(),
  recipient: z.string().trim().max(200).optional(),
  customer: z.string().trim().max(100).optional(),
  plate: z.string().trim().max(20).optional(),
  customerId: Id.optional(),
  vehicleId: Id.optional(),
  interventionId: Id.optional(),
  template: queryList(z.string().trim().min(1).max(100), 40).optional(),
  status: queryList(EmailDeliveryStatus, 4).optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(SENT_EMAILS_PAGE_SIZE),
});
export type SentEmailsQuery = z.infer<typeof SentEmailsQuery>;

export const SentEmailItem = z.object({
  id: Id,
  template: z.string(),
  /** Libellé du modèle (`EMAIL_TEMPLATE_LABELS`), sinon le nom technique. */
  templateLabel: z.string(),
  audience: SentEmailAudience,
  /** Adresse en clair ; `ERASED_MARK` après effacement du client. */
  recipient: z.string(),
  status: EmailDeliveryStatus,
  /** Motif d'échec ou d'annulation, lisible (`deliveryErrorLabel`). */
  error: z.string().nullable(),
  createdAt: Timestamp,
  sentAt: Timestamp.nullable(),
  /** Envoi différé (rappel, regroupement des photos) : heure prévue. */
  scheduledFor: Timestamp.nullable(),
  customer: z.object({ id: Id, name: z.string() }).nullable(),
  vehicle: z.object({ id: Id, plate: z.string(), label: z.string() }).nullable(),
  intervention: z.object({ id: Id, receivedAt: Timestamp, status: InterventionStatus }).nullable(),
  /** Client effacé : destinataire « [effacé] », contenu non consultable. */
  erased: z.boolean(),
  /** Le contenu peut être consulté (`deliveries.content`). */
  contentAvailable: z.boolean(),
});
export type SentEmailItem = z.infer<typeof SentEmailItem>;

export const SentEmailsPage = z.object({
  items: z.array(SentEmailItem),
  nextCursor: z.string().nullable(),
  /** Nombre d'e-mails pour le filtre courant (toutes pages). */
  total: z.int(),
});
export type SentEmailsPage = z.infer<typeof SentEmailsPage>;

/** Identifiant de l'envoi en paramètre de requête (`?id=`). */
export const SentEmailContentQuery = z.strictObject({ id: Id });

/** Liens neutralisés dans un contenu consulté (texte ; le bouton HTML perd son `href`). */
export const PREVIEW_GARAGE_LINK_PLACEHOLDER = '[lien vers l’espace du garage]';
export const PREVIEW_INVITATION_LINK_PLACEHOLDER = '[lien personnel d’invitation]';

export const SentEmailUnavailableReason = z.enum(['erased', 'not_reconstructible', 'amounts_hidden']);
export type SentEmailUnavailableReason = z.infer<typeof SentEmailUnavailableReason>;

/**
 * `deliveries.content` : contenu d'un e-mail du journal, **reconstitué** (le contenu exact n'est pas
 * conservé) à partir des données figées de l'envoi (modèle, événement d'origine, version du complément,
 * bilan figé…) et de la marque actuelle, sans aucun envoi ni jeton. Liens personnels neutralisés
 * (`PREVIEW_LINK_PLACEHOLDER`, `PREVIEW_INVITATION_LINK_PLACEHOLDER`, `PREVIEW_GARAGE_LINK_PLACEHOLDER`),
 * montants remplacés par `PREVIEW_AMOUNT_PLACEHOLDER` sans `amounts.view`. `content` vaut `null` avec
 * `unavailableReason` si le contenu ne peut pas être montré.
 */
export const SentEmailContentOutput = z.object({
  email: SentEmailItem,
  content: z.object({ subject: z.string(), text: z.string(), html: z.string() }).nullable(),
  /** Toujours vrai en V0 : rendu à nouveau, pas l'original archivé. */
  reconstructed: z.boolean(),
  unavailableReason: SentEmailUnavailableReason.nullable(),
  amountsHidden: z.boolean(),
});
export type SentEmailContentOutput = z.infer<typeof SentEmailContentOutput>;
